import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Bookmark, MapPin, Phone, Navigation, Stethoscope, Video, ShieldCheck,
  Building2, Clock, Search, BriefcaseMedical,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { hospitalApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";

const TEAL = "#005C66";
const TIME_SLOTS = ["09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "02:00 PM", "04:00 PM", "06:00 PM"];
const BM_KEY = "trata_bookmarked_hospitals";
const catLabel = (c) => c.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

export default function HospitalDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.customerProfile);

  const [loading, setLoading] = useState(true);
  const [hospital, setHospital] = useState(null);
  const [servicesByCat, setServicesByCat] = useState({});
  const [serviceQuery, setServiceQuery] = useState("");
  const [bookingSvc, setBookingSvc] = useState(null);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState(TIME_SLOTS[1]);
  const [submitting, setSubmitting] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([hospitalApi.detail(id), hospitalApi.services(id)])
      .then(([d, s]) => {
        if (!active) return;
        setHospital(d.data?.hospital || null);
        setServicesByCat(s.data?.services_by_category || {});
      })
      .catch(() => toast.error("Could not load hospital"))
      .finally(() => active && setLoading(false));
    try { setBookmarked(JSON.parse(localStorage.getItem(BM_KEY) || "[]").includes(id)); } catch { /* ignore */ }
    return () => { active = false; };
  }, [id]);

  const toggleBookmark = () => {
    let list = [];
    try { list = JSON.parse(localStorage.getItem(BM_KEY) || "[]"); } catch { /* ignore */ }
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    localStorage.setItem(BM_KEY, JSON.stringify(next));
    setBookmarked(next.includes(id));
  };

  const submitBooking = async () => {
    if (!date) return toast.error("Please choose a date");
    setSubmitting(true);
    try {
      await hospitalApi.bookService(id, bookingSvc.id, {
        user_id: user._id,
        patient_name: profile?.basicInfo?.fullName || user?.name || "",
        patient_phone: user?.phone || "",
        appointment_date: date,
        time_slot: slot,
      });
      toast.success("Service booked! Track it under For You.");
      setBookingSvc(null); setDate("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-28 rounded-3xl" /><Skeleton className="h-40 rounded-3xl" /><Skeleton className="h-40 rounded-3xl" /></div>;
  }
  if (!hospital) return <p className="text-muted-foreground">Hospital not found.</p>;

  const cityState = [hospital.city, hospital.state].filter(Boolean).join(", ") || hospital.address;
  const specializations = hospital.specializations || hospital.specialties || [];
  const doctors = hospital.specialistsCount || 0;
  const beds = hospital.beds || hospital.bedCount || 0;
  const specialities = specializations.length || hospital.specialitiesCount || 0;
  const mapsUrl = hospital.address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${hospital.name} ${hospital.address} ${hospital.city || ""}`)}` : null;

  const allServices = Object.values(servicesByCat).flat();
  const topServices = allServices.slice(0, 4);
  const q = serviceQuery.trim().toLowerCase();
  const filteredCats = Object.fromEntries(
    Object.entries(servicesByCat)
      .map(([cat, items]) => [cat, q ? items.filter((s) => (s.name || "").toLowerCase().includes(q)) : items])
      .filter(([, items]) => items.length > 0)
  );

  const quick = [
    { id: "doctor", label: "Book Doctor", Icon: Stethoscope, onClick: () => navigate(`/doctors?hospitalId=${id}&hospitalName=${encodeURIComponent(hospital.name)}`) },
    { id: "video", label: "Consult Online", Icon: Video, onClick: () => navigate(`/doctors?hospitalId=${id}&hospitalName=${encodeURIComponent(hospital.name)}&consultMode=video`) },
    { id: "call", label: "Call", Icon: Phone, onClick: () => (hospital.phone ? (window.location.href = `tel:${hospital.phone}`) : toast.info("Phone number not available")) },
    { id: "directions", label: "Directions", Icon: Navigation, onClick: () => (mapsUrl ? window.open(mapsUrl, "_blank") : toast.info("Address not available")) },
  ];

  return (
    <div className="space-y-5">
      {/* Header banner */}
      <div className="-mx-4 md:-mx-10 -mt-6 px-4 md:px-10 py-4 flex items-center justify-between text-white" style={{ backgroundColor: TEAL }}>
        <button onClick={() => navigate(-1)} data-testid="hospital-back"><ArrowLeft className="h-6 w-6" /></button>
        <span className="font-semibold truncate px-3">{hospital.name}</span>
        <button onClick={toggleBookmark} data-testid="hospital-bookmark">
          <Bookmark className="h-5 w-5" fill={bookmarked ? "#fff" : "none"} />
        </button>
      </div>

      {/* Profile summary */}
      <Card className="rounded-3xl p-5">
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: "#E6F3F4" }}>
            <Building2 className="h-8 w-8" style={{ color: TEAL }} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold truncate">{hospital.name}</h1>
            {hospital.hospitalType && <p className="text-sm text-muted-foreground capitalize">{hospital.hospitalType}</p>}
            <div className="flex items-center gap-1 mt-1 text-sm" style={{ color: TEAL }}>
              <ShieldCheck className="h-3.5 w-3.5" /> <span className="font-medium">Verified partner</span>
            </div>
            {cityState && <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="h-3.5 w-3.5" />{cityState}</p>}
          </div>
        </div>
        {/* Stats */}
        <div className="flex items-stretch mt-5 rounded-2xl bg-muted/60 py-3">
          {[{ v: doctors || "—", l: "Doctors" }, { v: beds || "—", l: "Beds" }, { v: specialities || "—", l: "Specialities" }].map((st, i) => (
            <div key={i} className="flex-1 flex flex-col items-center border-r border-border last:border-r-0">
              <p className="font-semibold">{st.v}</p>
              <p className="text-[11px] text-muted-foreground">{st.l}</p>
            </div>
          ))}
          <div className="flex-1 flex flex-col items-center">
            <Clock className="h-4 w-4" style={{ color: "#2563EB" }} />
            <p className="text-[11px] text-muted-foreground mt-0.5">24x7 ER</p>
          </div>
        </div>
      </Card>

      {/* Quick actions */}
      <div className="grid grid-cols-4 gap-2">
        {quick.map((a) => (
          <button key={a.id} onClick={a.onClick} data-testid={`quick-${a.id}`} className="flex flex-col items-center gap-2 py-2">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "#E6F3F4" }}>
              <a.Icon className="h-5 w-5" style={{ color: TEAL }} />
            </div>
            <span className="text-xs font-medium text-center">{a.label}</span>
          </button>
        ))}
      </div>

      {/* Top Services */}
      {topServices.length > 0 && (
        <section>
          <h2 className="text-base font-semibold mb-3">Top Services</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {topServices.map((s) => (
              <Card key={s.id} className="rounded-2xl p-4">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#E6F3F4" }}>
                  <BriefcaseMedical className="h-5 w-5" style={{ color: TEAL }} />
                </div>
                <p className="text-sm font-medium mt-2 line-clamp-2">{s.name}</p>
                {(s.discounted_price || s.price) ? <p className="text-sm font-semibold mt-1" style={{ color: TEAL }}>₹{s.discounted_price || s.price}</p> : null}
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Departments */}
      {specializations.length > 0 && (
        <section>
          <h2 className="text-base font-semibold mb-3">Departments</h2>
          <div className="flex flex-wrap gap-2">
            {specializations.map((d, i) => (
              <span key={`${d}-${i}`} className="text-sm px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground">{d}</span>
            ))}
          </div>
        </section>
      )}

      {/* All Services */}
      <section>
        <h2 className="text-base font-semibold mb-3">All Services &amp; Care</h2>
        <div className="flex items-center rounded-2xl bg-muted px-4 h-11 mb-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input value={serviceQuery} onChange={(e) => setServiceQuery(e.target.value)} placeholder="Search services..." className="border-0 bg-transparent focus-visible:ring-0 shadow-none" data-testid="service-search" />
        </div>
        {Object.keys(filteredCats).length > 0 ? (
          <div className="space-y-4">
            {Object.entries(filteredCats).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-sm font-medium text-muted-foreground mb-2">{catLabel(cat)}</p>
                <div className="space-y-2">
                  {items.map((s) => (
                    <Card key={s.id} className="rounded-2xl p-3 flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: "#E6F3F4" }}>
                        <Stethoscope className="h-5 w-5" style={{ color: TEAL }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{s.name}</p>
                        {s.description && <p className="text-xs text-muted-foreground truncate">{s.description}</p>}
                        {(s.discounted_price || s.price) ? <p className="text-sm font-semibold" style={{ color: TEAL }}>₹{s.discounted_price || s.price}</p> : null}
                      </div>
                      <Button size="sm" className="rounded-full" onClick={() => { setDate(""); setSlot(TIME_SLOTS[1]); setBookingSvc(s); }} data-testid={`book-service-${s.id}`}>Book</Button>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card className="rounded-3xl p-6 text-center border-dashed">
            <p className="text-sm text-muted-foreground">{q ? "No services match your search." : "This hospital has not listed services yet."}</p>
          </Card>
        )}
      </section>

      {/* Contact */}
      {hospital.phone && (
        <a href={`tel:${hospital.phone}`} data-testid="call-hospital" className="flex items-center justify-center gap-2 h-12 rounded-2xl border border-border bg-card font-medium" style={{ color: TEAL }}>
          <Phone className="h-5 w-5" /> Call Hospital
        </a>
      )}

      {/* Booking dialog */}
      <Dialog open={!!bookingSvc} onOpenChange={(o) => !o && setBookingSvc(null)}>
        <DialogContent className="rounded-3xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Book {bookingSvc?.name}</DialogTitle>
            <DialogDescription>Choose a date and time. Pay at the hospital.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="bdate">Date</Label>
              <Input id="bdate" type="date" value={date} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} className="mt-1.5 rounded-2xl h-11" data-testid="booking-date" />
            </div>
            <div>
              <Label>Time slot</Label>
              <Select value={slot} onValueChange={setSlot}>
                <SelectTrigger className="mt-1.5 rounded-2xl h-11" data-testid="booking-slot"><SelectValue /></SelectTrigger>
                <SelectContent>{TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button className="w-full rounded-2xl h-11 mt-2" onClick={submitBooking} disabled={submitting} data-testid="confirm-service-booking">
            {submitting ? "Booking..." : "Confirm Booking"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
